import { useCallback, useEffect, useRef, useState } from "react";
import { rememberRoomVisit } from "@/hooks/useSavedRooms";
import type { RoomMode } from "@/engine/state/roomControl";
import { trackEvent } from "@/engine/diagnostics/analytics";
import {
  reportConversion,
  type ConversionName,
} from "@/engine/diagnostics/adsConsent";
import {
  currentAccountEmail,
  currentAccountId,
  noteCreatedRoom,
} from "./useAuthSession";

/**
 * Reports one ad conversion with the signed-in address attached when there is
 * one. Nothing waits for it: the lookup, the consent check and the tag are all
 * beside the walk into the room, never in front of it.
 */
function announceConversion(name: ConversionName): void {
  void (async () => {
    const email = await currentAccountEmail();
    await reportConversion(name, fetch, email);
  })();
}

import {
  isDisplayNameCustom,
  setDisplayName,
  setSuggestedDisplayName,
} from "./useDisplayName";
import {
  endRoomSession,
  resolveRoomEntry,
  startRoomSession,
} from "@/engine/diagnostics/roomTrace";
import { generateRoomCode, isRoomCode } from "@/engine/state/roomIdentity";
import { isCampaignRoom, type EntryRefusal } from "@/engine/state/roomAccess";
import { prefetchedRoomAccess } from "@/adapters/supabase/roomPrefetch";
import {
  releaseHandedCapture,
  type HandedCapture,
} from "@/engine/media/thresholdCapture";
import {
  markEntryStage,
  resetEntryTiming,
  startEntryTiming,
} from "@/engine/diagnostics/entryTiming";
import { getRingSpawnPosition } from "@/engine/scene/ring";
import {
  forgetOwnedRoom,
  rememberOwnedRoom,
} from "@/engine/state/localOwnership";
import { rememberRemoval } from "@/engine/state/roomRemovals";

/**
 * Getting into a room and back out of it.
 *
 * A room only exists while somebody is in it, so joining an unused code and
 * creating one are the same operation as far as the backend is concerned. The
 * difference is only visible afterwards, in whether anybody else turns up —
 * which is why the *outcome* is recorded once presence has settled rather than
 * at the moment of joining. Analytics that could not tell "joined a live room"
 * from "opened an empty one" would say almost nothing.
 */

/** How long presence gets to settle before an entry counts as "empty room". */
const ENTRY_RESOLVE_DELAY_MS = 4000;

export type AppStep = "entry" | "room";

/**
 * How a walk into a room began, while it is still going on.
 *
 * The two differ in one thing only, and it is a thing about the screen rather
 * than about the room: what the visitor was looking at a moment ago. Somebody
 * who pressed the way in is looking at the door, so the door stays for a second
 * before the waits replace it. Somebody coming back from a sign-in arrives on a
 * page that remembers nothing, and a door they never asked for, shown for a
 * second and taken away, is the flash the intent in the address exists to
 * prevent.
 */
export type EntryWalkIn = "pressed" | "resumed";

export interface RoomEntryOptions {
  /**
   * True when the walk in this hook is mounted for was already running on the
   * entry screen — a sign-in round trip coming home to an invitation. The card
   * is showing it before this half of the application is even fetched, and it
   * must not be taken back to the door in order to be shown it again.
   */
  resumed?: boolean;
  /**
   * True when the room's row was refused before the walk began — the press
   * that created it writes the row now, and this is how the room hears about a
   * refusal it did not see.
   */
  recordRefused?: boolean;
  localPeerId: string;
  /** True once the voice transport is up. */
  connected: boolean;
  /** How many other people are visibly here. */
  peerCount: number;
  /**
   * Resolves with the code of the room actually entered (generated on create).
   * What the threshold handed over is the room session's from this call on.
   */
  join: (roomId?: string, handed?: HandedCapture | null) => Promise<string>;
  leave: () => void;
  /** Where the local avatar starts before it snaps onto the ring. */
  onSpawn: (position: { x: number; y: number }) => void;
  /** Called on the way out, before the transport is torn down. */
  onLeave: () => void;
  /** Room-scoped state the next room must start without. */
  onReset: () => void;
}

export interface RoomEntry {
  step: AppStep;
  roomCodeInput: string;
  isCreatingRoom: boolean;
  /** Why the last attempt to join was refused, when it was. */
  entryError: EntryRefusal | null;
  /**
   * True when the database refused this room's row. The room itself is up and
   * everybody inside is unaffected; its code admits nobody, which only the
   * host who created it can do anything about.
   */
  roomNotRecorded: boolean;
  /**
   * Set while the visitor is being walked into a room and the entry card is
   * showing it, and says how the walk began; null when no walk is going on.
   */
  walkIn: EntryWalkIn | null;
  setRoomCodeInput: (code: string) => void;
  /** The code was settled where the press was; this half opens that room. */
  createRoom: (
    code: string,
    userName: string,
    handed?: HandedCapture | null,
  ) => Promise<void>;
  joinRoom: (
    code: string,
    userName: string,
    handed?: HandedCapture | null,
  ) => Promise<void>;
  leaveRoom: () => void;
  /**
   * The host removed this visitor from the room they were standing in. The same
   * way out as leaving, and then the door says why — which is all they are told:
   * not who pressed it, and not why they did.
   */
  removedByHost: () => void;
  /**
   * Abandons a walk in that has not finished. The same teardown as leaving,
   * under its own name: a visitor who never arrived has not left anything.
   */
  cancelEntry: () => void;
  changeName: (value: string) => void;
}

/**
 * Everything that has to be told the visitor is in — analytics, the ad
 * conversion beside it so the two can never disagree, and the note about a
 * room that was just created.
 */
function announceEntered(kind: "create" | "join", roomId: string | undefined) {
  trackEvent(kind === "create" ? "room-created" : "room-joined", { roomId });
  // Self-censors when consent is denied.
  announceConversion(kind === "create" ? "room-created" : "room-joined");
  // A campaign lands on a room of its own, and pressing Connect for it is the
  // whole conversion that campaign pays for — reported under its own name so
  // it can never be confused with an ordinary join.
  if (kind === "join" && isCampaignRoom(roomId)) {
    announceConversion("demo-room-joined");
  }
}

/**
 * Writing the room down, for whoever pressed Create.
 *
 * The row is written before the visitor is taken to the room's address (#610),
 * so the code in the address bar is a code that exists — but **the write is
 * still not allowed to bar the way in**. A refusal comes back as `false` and
 * the walk goes ahead; the host is told inside the room, because the code they
 * are about to share opens nothing and nobody else can find that out in time to
 * help.
 *
 * Ownership is remembered before the write is attempted, for the same reason:
 * hosting the room just created must not depend on a backend round trip.
 */
export async function noteRoomCreated(
  roomId: string,
  roomMode: RoomMode,
  /** The account the press waited for, rather than a second lookup of it. */
  ownerId?: string | null,
  /** What the host called the room, if anything. */
  displayName?: string,
): Promise<boolean | "taken"> {
  rememberOwnedRoom(roomId);
  const outcome = await noteCreatedRoom(roomId, roomMode, ownerId, displayName);
  // Somebody else's room already has this code: not ours, and not to enter.
  if (outcome === "taken") {
    forgetOwnedRoom(roomId);
    return "taken";
  }
  if (outcome !== "refused") return true;
  trackEvent("room-record-refused", { roomId });
  return false;
}

/**
 * Which room was actually entered, and telling the screen when it is not the one
 * that was asked for.
 *
 * `join` answers with the code it used, which is the requested one unless the
 * transport had a reason of its own; the fallbacks behind it are for a transport
 * that answered with nothing at all.
 */
function settleRoomId(
  joinedCode: string,
  requested: string,
  onDiffers: (settled: string) => void,
): string | undefined {
  const roomId = joinedCode || requested || undefined;
  if (roomId && roomId !== requested) onDiffers(roomId);
  return roomId;
}

/** A join is refused only when the record positively says the room is closed. */
async function isRefused(kind: "create" | "join", code: string | undefined) {
  if (kind !== "join" || !code) return false;
  return (await prefetchedRoomAccess(code)) === "closed";
}

/**
 * Why this attempt cannot go ahead, in the words the screen will use — or null
 * when nothing stands in its way.
 *
 * Creating is for a named visitor: the database refuses an anonymous insert, so
 * the screen says so before the attempt rather than after it. Joining asks the
 * record instead, and a lookup that could not be made lets the visitor in.
 *
 * The account is looked up through whoever asks: a visitor coming back from the
 * identity provider has a session still being restored, and asking the plain
 * way answers "nobody" to somebody who has just signed in.
 */
async function refuseEntry(
  kind: "create" | "join",
  code: string | undefined,
  lookupAccount: () => Promise<string | null> = currentAccountId,
): Promise<EntryRefusal | null> {
  // Before anything else is asked: no account and no row can make such a code
  // a room, and its channels must never be joined (#693).
  if (code && !isRoomCode(code)) {
    if (kind === "create") trackEvent("room-creation-refused");
    else trackEvent("room-join-refused", { roomId: code });
    return "invalid-code";
  }
  if (kind === "create") {
    if (await lookupAccount()) return null;
    trackEvent("room-creation-refused");
    return "needs-account";
  }
  if (await isRefused(kind, code)) {
    trackEvent("room-join-refused", { roomId: code });
    return "room-closed";
  }
  return null;
}

export function useRoomEntry(options: RoomEntryOptions): RoomEntry {
  const {
    resumed,
    recordRefused,
    localPeerId,
    connected,
    peerCount,
    join,
    leave,
    onSpawn,
    onLeave,
    onReset,
  } = options;

  const [step, setStep] = useState<AppStep>("entry");

  /**
   * Only an invitation resumes into a room; a returning host lands back on the
   * entry screen and presses Create themselves.
   *
   * Seeded rather than raised by the walk itself, because the entry screen has
   * been drawing this walk since before this half of the application was
   * fetched, and a frame of the door in the middle of it is the flash the
   * intent in the address exists to prevent.
   */
  const [walkIn, setWalkIn] = useState<EntryWalkIn | null>(
    resumed ? "resumed" : null,
  );

  // An invitation is an address of its own now (#610), so nothing is read out
  // of the address bar here: the code this room was entered with is the code it
  // was mounted for.
  const [enteredRoomCode, setEnteredRoomCode] = useState<string | null>(null);
  const roomCodeInput = enteredRoomCode ?? "";
  // The `useState` setter under a name that pairs with `roomCodeInput`. It is
  // referentially stable, but `react-hooks` v7 only trusts that of the setter
  // identifier itself, so callbacks that use this alias list it in their deps.
  const setRoomCodeInput = setEnteredRoomCode;
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  /** Remembered so leaving can pre-fill the Join tab with the room just left. */
  const [lastRoomId, setLastRoomId] = useState("");
  /** One "landed among people" conversion per visit, not per presence change. */
  const populatedRoomReported = useRef(false);
  /** Set only when a join was refused, cleared the moment one is attempted. */
  const [entryError, setEntryError] = useState<EntryRefusal | null>(null);
  /** Set only for the host, and only when the row came back refused. */
  const [roomNotRecorded, setRoomNotRecorded] = useState(
    Boolean(recordRefused),
  );
  /**
   * Which walk in is the current one.
   *
   * The way in is now held on the entry screen while the access check answers,
   * and that is long enough to be cancelled — or pressed again after a refusal.
   * The check cannot be called off once it is in flight, so the walk that
   * started it carries a number, and an answer arriving for a number nobody is
   * waiting on any more is dropped rather than acted on. Without it, cancelling
   * during the hold puts the visitor in the room a moment later anyway.
   */
  const walkNumber = useRef(0);

  const enter = useCallback(
    async (
      code: string | undefined,
      userName: string,
      kind: "create" | "join",
      handed?: HandedCapture | null,
    ) => {
      const trimmed = userName.trim();
      // Entering under the offered nickname does not make it a typed name:
      // shuffling the avatar inside the room should still rename with it.
      if (isDisplayNameCustom()) setDisplayName(trimmed);
      else setSuggestedDisplayName(trimmed);
      setIsCreatingRoom(kind === "create");
      setEntryError(null);
      setRoomNotRecorded(false);
      // The press. Every step of the way in is measured from here.
      startEntryTiming(kind);

      // The code is settled here rather than inside `join`, because the room is
      // shown before the microphone and the channel are ready and it has to be
      // able to name itself from the first frame.
      const requested = (code || generateRoomCode()).trim().toLowerCase();
      setRoomCodeInput(requested);
      setLastRoomId(requested);

      trackEvent(
        kind === "create" ? "room-creation-started" : "room-join-started",
        { roomId: requested },
      );

      // The card says it is walking somebody in, on this path as much as on the
      // return from an account. A seeded `resumed` wins: a return that is
      // already walking in did not begin with anybody pressing anything.
      setWalkIn((began) => began ?? "pressed");
      const walk = (walkNumber.current += 1);
      const abandoned = () => walk !== walkNumber.current;
      try {
        // The one wait that can answer no is waited for out here, with the door
        // still on screen. Decision 0009: the access check may hold the way in
        // because it can refuse it; the channel and the first audio may not,
        // because all they can do is take time. Showing the room first meant
        // putting a refused visitor into one and then taking them back out.
        const refusal = await refuseEntry(kind, requested);
        markEntryStage("session-checked");

        // Nobody is waiting on this answer any more: the walk was cancelled, or
        // the visitor pressed again and a later one owns the card now. Neither
        // a room nor a refusal belongs to them. What the threshold handed over
        // stays with the request, which a later walk may still take up.
        if (abandoned()) return;

        if (refusal) {
          // Refused: no walk for this request will ever join, so what it
          // carries is stopped rather than left open behind a refusal.
          releaseHandedCapture(handed);
          resetEntryTiming();
          // Nothing was joined and the room was never shown, so naming the
          // reason is the whole undo.
          setIsCreatingRoom(false);
          setEntryError(refusal);
          return;
        }

        // In. The ring-snap effect walks from here to the nearest free spot,
        // and what is still being waited for shows as "connecting" inside the
        // room rather than as a screen the visitor has to sit through.
        onSpawn(getRingSpawnPosition());
        setStep("room");

        // `join` resolves with the room code it actually used, which is the
        // requested one unless the transport had a reason of its own.
        const joinedCode = await join(requested, handed);
        markEntryStage("channel-connected");
        const roomId = settleRoomId(joinedCode, requested, (settled) => {
          setRoomCodeInput(settled);
          setLastRoomId(settled);
        });
        // A walk in abandoned while the channel was subscribing is not a visit,
        // and counting it as one would hide the cancellations inside the joins.
        if (abandoned()) return;
        // Nothing on screen waits for these: the visitor is already in.
        announceEntered(kind, roomId);
        startRoomSession(roomId ?? "unknown", localPeerId || "local", kind);
        // The rail's list is where you have been and what you made. Recorded
        // on arrival rather than on the press: a walk in that was refused is
        // not a room anybody was in.
        if (roomId) rememberRoomVisit(roomId);
      } finally {
        // Every ending clears it, including one that threw: a card left saying
        // it is walking somebody in, when nothing is, has no way back. A later
        // walk clears its own — this one must not clear the card out from under
        // it.
        if (!abandoned()) setWalkIn(null);
      }
    },
    [join, localPeerId, onSpawn, setRoomCodeInput],
  );

  /**
   * Everything leaving does apart from reporting it.
   *
   * Cancelling a walk in and leaving a room tear down exactly the same things,
   * and they say two different things about what happened: one visit ended,
   * one never began. Sharing the teardown keeps them from drifting; keeping the
   * report out of it keeps the funnel honest.
   */
  const standDown = useCallback(() => {
    resetEntryTiming();
    onLeave();
    leave();
    // Stepping back closes the room's channels: their hooks own the
    // subscriptions and tear them down when this goes false.
    setStep("entry");
    onReset();
    setRoomCodeInput(lastRoomId);
    setIsCreatingRoom(false);
    // Whatever walk in was going on is over, and an answer still owed to it
    // arrives to nobody.
    walkNumber.current += 1;
    setWalkIn(null);
    setEntryError(null);
    setRoomNotRecorded(false);
    populatedRoomReported.current = false;
  }, [lastRoomId, leave, onLeave, onReset, setRoomCodeInput]);

  const leaveRoom = useCallback(() => {
    console.log("Leaving room");
    trackEvent("room-left");
    endRoomSession("user_left");
    standDown();
  }, [standDown]);

  const removedByHost = useCallback(() => {
    // Before anything is torn down, so a reload in the middle of it still
    // finds the room's threshold shut.
    if (lastRoomId) rememberRemoval(lastRoomId);
    trackEvent("room-left");
    endRoomSession("removed_by_host");
    standDown();
    // After `standDown`, which clears whatever the last attempt to join said.
    setEntryError("removed-by-host");
  }, [lastRoomId, standDown]);

  const cancelEntry = useCallback(() => {
    console.log("Cancelling entry");
    // Not `room-left`: nothing was entered, and counting a cancelled walk in as
    // a visit that ended would hide the failures inside the leaves.
    endRoomSession("entry_cancelled");
    standDown();
  }, [standDown]);

  // Record the outcome once presence has settled — see the note above.
  useEffect(() => {
    if (step !== "room" || !connected) return;
    // Audio is live: the last stage of the walk in has passed.
    markEntryStage("audio-started");
    if (peerCount > 0) {
      resolveRoomEntry(peerCount);
      // Landing among people is the outcome worth advertising for, and it is
      // reported once per visit however often presence changes afterwards.
      if (!populatedRoomReported.current) {
        populatedRoomReported.current = true;
        announceConversion("populated-room-entered");
      }
      return;
    }
    const timer = window.setTimeout(
      () => resolveRoomEntry(0),
      ENTRY_RESOLVE_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [step, connected, peerCount]);

  return {
    step,
    roomCodeInput,
    isCreatingRoom,
    entryError,
    roomNotRecorded,
    walkIn,
    setRoomCodeInput,
    createRoom: (code, userName, handed) =>
      enter(code, userName, "create", handed),
    joinRoom: (code, userName, handed) => enter(code, userName, "join", handed),
    leaveRoom,
    removedByHost,
    cancelEntry,
    changeName: useCallback((value: string) => setDisplayName(value), []),
  };
}
